import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Mail, Lock, EyeOff, Eye, ArrowLeft } from 'lucide-react'
import AuthLayout from '@/components/AuthLayout'
import { useAuth } from '@/context/AuthContext'

interface FieldErrors {
  email?: string
  password?: string
}

function validateLogin(email: string, password: string): FieldErrors {
  const errors: FieldErrors = {}
  if (!email.trim()) {
    errors.email = 'И-мэйл хаяг оруулна уу'
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    errors.email = 'Зөв и-мэйл хаяг оруулна уу'
  }
  if (!password) {
    errors.password = 'Нууц үг оруулна уу'
  }
  return errors
}

export default function Login() {
  const [showPassword, setShowPassword] = useState(false)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({})
  const [loading, setLoading] = useState(false)
  const navigate = useNavigate()
  const { login, loginWithFacebook, loginWithGoogle } = useAuth()

  const handleGoogleLogin = async () => {
    setError('')
    setLoading(true)
    try {
      const result = await loginWithGoogle('login')
      if (!result.requireOtp) navigate('/')
    } catch (err: any) {
      setError(err.message || 'Google нэвтрэлт амжилтгүй')
    } finally {
      setLoading(false)
    }
  }

  const handleFacebookLogin = async () => {
    setError('')
    setLoading(true)
    try {
      await loginWithFacebook()
      navigate('/')
    } catch (err: any) {
      setError(err.message || 'Facebook нэвтрэлт амжилтгүй')
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async () => {
    setError('')
    const errs = validateLogin(email, password)
    setFieldErrors(errs)
    if (Object.keys(errs).length > 0) return

    setLoading(true)
    try {
      await login(email, password)
      navigate('/')
    } catch (err: any) {
      setError(err.message || 'Нэвтрэх үед алдаа гарлаа')
    } finally {
      setLoading(false)
    }
  }

  return (
    <AuthLayout>
      <div className="w-full max-w-[550px] flex flex-col gap-8">
        <div className="flex flex-col gap-2">
          <h1 className="text-2xl font-bold text-black">Тавтай морил</h1>
          <p className="text-sm text-gray-600">
            Платформд нэвтрэх эсвэл шинээр бүртгүүлнэ үү.
          </p>
        </div>

        <div className="bg-gray-100 rounded-full p-1 flex gap-1">
          <button className="flex-1 px-6 py-2.5 rounded-full text-center text-base font-medium bg-white shadow-md text-black transition">
            Нэвтрэх
          </button>
          <button
            onClick={() => navigate('/register')}
            className="flex-1 px-6 py-2.5 rounded-full text-center text-base font-medium text-gray-400 transition hover:text-gray-600"
          >
            Бүртгүүлэх
          </button>
        </div>

        {error && (
          <div className="bg-red-50 text-red-600 text-sm rounded-xl px-4 py-3">{error}</div>
        )}

        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-gray-700">И-мэйл хаяг</label>
            <div className={`bg-white border rounded-full px-3.5 py-2.5 shadow-xs flex items-center gap-2 transition ${fieldErrors.email ? 'border-red-400' : 'border-gray-200 focus-within:border-primary-500 focus-within:ring-1 focus-within:ring-primary-500'}`}>
              <Mail className="size-[18px] text-gray-400 shrink-0" />
              <input
                type="email"
                placeholder="info@gmail.com"
                value={email}
                onChange={e => {
                  setEmail(e.target.value)
                  if (fieldErrors.email) setFieldErrors(prev => ({ ...prev, email: undefined }))
                }}
                className="flex-1 outline-none text-base text-gray-900 placeholder:text-gray-300 bg-transparent"
              />
            </div>
            {fieldErrors.email && <p className="text-xs text-red-500 ml-1">{fieldErrors.email}</p>}
          </div>

          <div className="flex flex-col gap-1.5">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-gray-700">Нууц үг</label>

              <div className="flex items-center gap-3">
                <Link
                  to="/verify-email"
                  className="text-sm font-medium text-primary-500 hover:text-primary-600"
                >
                  Имэйл баталгаажуулах
                </Link>

                <Link
                  to="/forgot-password"
                  className="text-sm font-medium text-primary-500 hover:text-primary-600"
                >
                  Нууц үг мартсан?
                </Link>
              </div>
            </div>
            <div className={`bg-white border rounded-full px-3.5 py-2.5 shadow-xs flex items-center gap-2 transition ${fieldErrors.password ? 'border-red-400' : 'border-gray-200 focus-within:border-primary-500 focus-within:ring-1 focus-within:ring-primary-500'}`}>
              <Lock className="size-[18px] text-gray-400 shrink-0" />
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder="••••••••"
                value={password}
                onChange={e => {
                  setPassword(e.target.value)
                  if (fieldErrors.password) setFieldErrors(prev => ({ ...prev, password: undefined }))
                }}
                onKeyDown={e => e.key === 'Enter' && handleSubmit()}
                className="flex-1 outline-none text-base text-gray-900 placeholder:text-gray-300 bg-transparent"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <Eye className="size-[18px]" /> : <EyeOff className="size-[18px]" />}
              </button>
            </div>
            {fieldErrors.password && <p className="text-xs text-red-500 ml-1">{fieldErrors.password}</p>}
          </div>
        </div>

        <button
          onClick={handleSubmit}
          disabled={loading}
          className="w-full bg-primary-500 text-white rounded-full px-[18px] py-3 text-base font-semibold shadow-xs hover:bg-primary-600 transition disabled:opacity-50"
        >
          {loading ? 'Нэвтэрч байна...' : 'Нэвтрэх'}
        </button>

        <div className="flex items-center gap-3 opacity-80">
          <div className="flex-1 h-px bg-gray-300" />
          <span className="text-xs font-semibold text-gray-400 uppercase">Эсвэл</span>
          <div className="flex-1 h-px bg-gray-300" />
        </div>

        <div className="flex gap-4">
          <button
            onClick={handleGoogleLogin}
            disabled={loading}
            className="flex-1 bg-white border border-gray-300 rounded-full px-[18px] py-3 shadow-xs flex items-center justify-center gap-2 hover:bg-gray-50 transition disabled:opacity-50"
          >
            <svg className="size-5" viewBox="0 0 20 20" fill="none">
              <path d="M18.171 8.368h-.67v-.035H10v3.333h4.71A4.998 4.998 0 0 1 5 10a5 5 0 0 1 5-5c1.275 0 2.434.48 3.317 1.267l2.357-2.357A8.295 8.295 0 0 0 10 1.667 8.334 8.334 0 0 0 1.667 10 8.334 8.334 0 0 0 10 18.333 8.334 8.334 0 0 0 18.333 10c0-.559-.057-1.104-.162-1.632z" fill="#FFC107"/>
              <path d="M2.627 6.121 5.365 8.13A4.998 4.998 0 0 1 10 5c1.275 0 2.434.48 3.317 1.267l2.357-2.357A8.295 8.295 0 0 0 10 1.667a8.33 8.33 0 0 0-7.373 4.454z" fill="#FF3D00"/>
              <path d="M10 18.333a8.294 8.294 0 0 0 5.587-2.163l-2.579-2.183A4.963 4.963 0 0 1 10 15a4.998 4.998 0 0 1-4.701-3.316l-2.72 2.095A8.328 8.328 0 0 0 10 18.333z" fill="#4CAF50"/>
              <path d="M18.171 8.368H17.5v-.035H10v3.333h4.71a5.017 5.017 0 0 1-1.703 2.321l2.58 2.183c-.183.166 2.746-2.003 2.746-6.17 0-.559-.057-1.104-.162-1.632z" fill="#1976D2"/>
            </svg>
            <span className="text-base font-semibold text-gray-700">Google</span>
          </button>
          {/* <button
            onClick={handleFacebookLogin}
            disabled={loading}
            className="flex-1 bg-white border border-gray-300 rounded-full px-[18px] py-3 shadow-xs flex items-center justify-center gap-2 hover:bg-gray-50 transition disabled:opacity-50"
          >
            <svg className="size-5" viewBox="0 0 20 20" fill="none">
              <path d="M20 10c0-5.523-4.477-10-10-10S0 4.477 0 10c0 4.991 3.657 9.128 8.438 9.878V12.89H5.898V10h2.54V7.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V10h2.773l-.443 2.89h-2.33v6.988C16.343 19.128 20 14.991 20 10z" fill="#1877F2"/>
              <path d="M13.893 12.89 14.336 10h-2.773V8.124c0-.79.387-1.563 1.63-1.563h1.26v-2.46s-1.144-.196-2.238-.196c-2.284 0-3.777 1.384-3.777 3.89V10H5.898v2.89h2.54v6.988a10.07 10.07 0 0 0 3.124 0V12.89h2.33z" fill="#fff"/>
            </svg>
            <span className="text-base font-semibold text-gray-700">Facebook</span>
          </button> */}
        </div>

        <Link
          to="/"
          className="flex items-center justify-center gap-2 text-sm font-semibold text-gray-600 hover:text-gray-800"
        >
          <ArrowLeft className="size-5" />
          Нүүр хуудасруу буцах
        </Link>
      </div>
    </AuthLayout>
  )
}
